import { Library } from "features/library/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { upsertLibrary } from "redux/index";
import Editor from "shared/components/Editor/Editor";
import { FlexContainer, TitleInput } from "shared/cx-components/cx-components.style";
import { SearchBar } from "shared/ui-basics/index";
import styled from "styled-components/macro";
import { encode } from "utils/helpers";

interface CustomLibraryTopSectionProps {
	library: Library;
	filterValue: string;
	handleFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	isCreate: boolean;
}

export const CustomLibraryTopSection: React.FC<CustomLibraryTopSectionProps> = ({ library, filterValue, handleFilterChange, isCreate }) => {
	const { t } = useTranslation();
	const libraries = useSelector((state: any) => state.libraries.customLibraries);

	const [libraryName, setLibraryName] = useState<string>(library?.name || "");
	const [description, setDescription] = useState<string>(library?.description || "");

	const Search = SearchBar({ filterValue, handleChange: handleFilterChange, size: "small", label: `SEARCH`, floatLabel: false });

	const handleUpsert = () => {
		upsertLibrary({ library: { ...library, name: encode(libraryName), description: encode(description) } });
	};

	const handleNameBlur = () => {
		if (libraryName === "") {
			setLibraryName(library?.name || "");
			return;
		}
		if (libraryName === library?.name) return;

		const defaultLibrariesNames = ["Touchpoints", "Stickers", "Brand Values", "Measures", "Systems", "Departments"];
		const isNameExist = libraries?.some((it: Library) => it?.id !== library?.id && it?.name?.toUpperCase() === libraryName?.toUpperCase());

		if (isNameExist || defaultLibrariesNames.includes(libraryName)) {
			return toast.info(t("NAME_EXISTS"));
		}

		handleUpsert();
	};

	return (
		<Container>
			<Wrapper>
				<TitleInput
					value={libraryName}
					title={libraryName}
					color="#595959"
					fontSize="24px"
					fontWeight={600}
					className="ps-0"
					name="name"
					placeholder={t("NEW_LIBRARY")}
					onBlur={handleNameBlur}
					onChange={(e) => setLibraryName(e.target.value)}
					autoFocus={isCreate}
					disabled={!library?.isCustom}
				/>
				<SearchContainer>{Search}</SearchContainer>
			</Wrapper>

			<FlexContainer className="p-1 my-3" focus={true}>
				{library?.isCustom && (
					<Editor
						value={description}
						onChange={(html: string) => setDescription(html)}
						onUpdate={() => {
							if (description !== library?.description) handleUpsert();
						}}
						placeholder={`${t("LIBRARY_DESCRIPTION")} (${t("OPTIONAL")})`}
						disabled={!library?.isCustom}
					/>
				)}
			</FlexContainer>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	margin-top: 32px;
	border-bottom: 1px solid #efefef;
`;

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	justify-content: space-between;
	width: 100%;
	height: 42px;
`;

const SearchContainer = styled.div`
	width: 268px;
`;
